/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { formatReceivedValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import TextArea from 'antd/lib/input/TextArea';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import {
  getVacacionesEmpleadoDiasDisfutados,
  getVacacionesEmpleadosDiasDisfrutados,
  postVacacionesEmpleadoDiasDisfutados,
  patchVacacionesEmpleadoDiasDisfutados,
  deleteVacacionesEmpleadoDiasDisfutados,
} from 'api/nominas/vacaciones/vacacionesEmpleadosDiasDisfrutados';
import { getVacacionesEmpleadosPeriodos } from 'api/nominas/vacaciones/vacacionesEmpleadosPeriodos';
import { buildDateRange } from 'api/configuraciones/ConfigurarGuardiasVacacionales/RegistroGuardias/RegistroGuardiasFechas';
import { getGuardiasVacacionalesGeneral } from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesGeneral';
import { getGuardiasVacacionalesPeriodos } from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesPeriodos';
import { integerRegExp } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import VacacionesRegistrosFechas from './VacacionesRegistrosFechas';

export const VacacionesRegistrosEmpleadosPermission = {
  permissionModel: 'vacacionempleadodiadisfrutado',
  catalogsModels: [],
  tabsModels: [],
};

const { Text } = Typography;

const VacacionesRegistrosEmpleados = ({
  permission, selectedEmpleado, autoEditRecordId, hideTables, onHideTableClose,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);
  const [vacacionesEmpleadosPeriodos, setVacacionesEmpleadosPeriodos] = useState([]);
  const [selectedVacacionEmpleado, setSelectedVacacionEmpleado] = useState();
  const [dates, setDates] = useState([]);
  const [guardiasVacacionalGeneral, setGuardiasVacacionalGeneral] = useState([]);
  const [periodosVacacionales, setPeriodosVacacionales] = useState([]);

  const formInstances = [
    form,
  ];

  const fechaInicialWatcher = Form.useWatch('fecha_inicial', form);
  const fechaFinalWatcher = Form.useWatch('fecha_final', form);
  const anoWatcher = Form.useWatch('ano', form);
  const configurarGuardiasVacacionalesGeneralWatcher = Form.useWatch('configurar_guardias_vacacionales_general', form);
  const configurarGuardiasVacacionalesWatcher = Form.useWatch('configurar_guardias_vacacionales', form);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      return values.map((e) => formatReceivedValues(e));
    }
    return formatReceivedValues(values);
  };

  // El backend anida configurar_guardias_vacacionales_general/configurar_guardias_vacacionales
  // (para que las columnas de la tabla puedan mostrar su descripción), pero los Select del
  // formulario necesitan el id plano para calzar con su dataSource.
  const flattenGuardiasVacacionesIds = (record) => ({
    ...record,
    configurar_guardias_vacacionales_general: record?.configurar_guardias_vacacionales_general?.id
      ?? record?.configurar_guardias_vacacionales_general,
    configurar_guardias_vacacionales: record?.configurar_guardias_vacacionales?.id
      ?? record?.configurar_guardias_vacacionales,
  });

  const fetchData = async () => {
    setLoading(true);
    const [_data, _periodos] = await Promise.all([
      getVacacionesEmpleadosDiasDisfrutados({ empleado: selectedEmpleado[0] }),
      getVacacionesEmpleadosPeriodos({ empleado: selectedEmpleado[0] }),
    ]);
    setData(formatReceivedClone(_data));
    setVacacionesEmpleadosPeriodos(_periodos);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _vacacionesEmpleadosPeriodos,
        _vacacionesEmpleadosDiasDisfrutados,
        _guardiasVacacionalGeneral,
      ] = await Promise.all([
        getEstadosGlobales(),
        getVacacionesEmpleadosPeriodos({ empleado: selectedEmpleado[0] }),
        getVacacionesEmpleadosDiasDisfrutados({ empleado: selectedEmpleado[0] }),
        getGuardiasVacacionalesGeneral({ estados_globales: ESTATUS_AUTORIZADO }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setVacacionesEmpleadosPeriodos(_vacacionesEmpleadosPeriodos);
        setData(formatReceivedClone(_vacacionesEmpleadosDiasDisfrutados));
        setGuardiasVacacionalGeneral(_guardiasVacacionalGeneral);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let mounted = true;
    if (configurarGuardiasVacacionalesGeneralWatcher === undefined
      || configurarGuardiasVacacionalesGeneralWatcher === null) {
      setPeriodosVacacionales([]);
      return undefined;
    }
    getGuardiasVacacionalesPeriodos({
      estados_globales: ESTATUS_AUTORIZADO,
      configurar_guardias_vacacionales_general: configurarGuardiasVacacionalesGeneralWatcher,
    }).then((_periodos) => {
      if (mounted) setPeriodosVacacionales(_periodos);
    });
    return () => { mounted = false; };
  }, [configurarGuardiasVacacionalesGeneralWatcher]);

  // Llenado automático de Fecha Inicial/Fecha Final a partir del Periodo Vacacional
  // seleccionado. Solo aplica al capturar (no al editar), para no pisar el rango real
  // ya guardado de un registro existente.
  // Las fechas de vigencia siempre se toman del periodo (no se guardan en el registro).
  useEffect(() => {
    if (!configurarGuardiasVacacionalesWatcher) return;
    const periodoVacacional = periodosVacacionales?.find(
      (p) => p.id === configurarGuardiasVacacionalesWatcher,
    );
    if (!periodoVacacional) return;
    const vigencia = {
      fecha_inicial_vigencia_vacaciones: periodoVacacional?.fecha_inicial_vigencia_vacaciones
        ? moment(periodoVacacional.fecha_inicial_vigencia_vacaciones) : null,
      fecha_final_vigencia_vacaciones: periodoVacacional?.fecha_final_vigencia_vacaciones
        ? moment(periodoVacacional.fecha_final_vigencia_vacaciones) : null,
    };
    if (selectedRowKeys.length) {
      form.setFieldsValue(vigencia);
      return;
    }
    form.setFieldsValue({
      fecha_inicial: periodoVacacional?.fecha_inicial ? moment(periodoVacacional.fecha_inicial) : null,
      fecha_final: periodoVacacional?.fecha_final ? moment(periodoVacacional.fecha_final) : null,
      ...vigencia,
    });
    // eslint-disable-next-line
  }, [configurarGuardiasVacacionalesWatcher, periodosVacacionales, selectedRowKeys.length]);

  useEffect(() => {
    if (anoWatcher === undefined || anoWatcher === null || selectedRowKeys.length) return;
    const periodoConfig = vacacionesEmpleadosPeriodos.find((vep) => vep?.ano === anoWatcher);
    setSelectedVacacionEmpleado(periodoConfig?.id);
    form.setFieldValue('dias_con_derecho', periodoConfig?.dias_habiles_pendientes);
    // eslint-disable-next-line
  }, [anoWatcher, selectedRowKeys.length]);

  useEffect(() => {
    if (!fechaInicialWatcher || !fechaFinalWatcher) {
      setDates([]);
      return;
    }
    const rangeOfDates = buildDateRange(moment(fechaInicialWatcher), moment(fechaFinalWatcher), true);
    setDates(rangeOfDates.map((value) => ({ id: value.format('YYYY-MM-DD'), fecha: value })));
  }, [fechaInicialWatcher, fechaFinalWatcher]);

  const resetState = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setSelectedVacacionEmpleado();
    setDates([]);
  };

  const onCancel = () => {
    resetState();
    form.resetFields();
    if (hideTables && onHideTableClose) onHideTableClose();
  };

  const onClickEdit = async (responseData = null) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const match = responseData || data.find((item) => item.id === key);
      if (!match) {
        setLoading(false);
        return;
      }
      const _vacaciones = await getVacacionesEmpleadoDiasDisfutados(match.id);
      setTimeout(() => {
        if (_vacaciones) {
          form.resetFields();
          setTimeout(() => {
            const estado = _vacaciones?.estados_globales?.id ?? _vacaciones?.estados_globales;
            form.setFieldsValue(flattenGuardiasVacacionesIds(formatReceivedClone(_vacaciones)));
            setSelectedRowKeys([match.id]);
            setSelectedData({ ...formatReceivedClone(_vacaciones), estados_globales: estado });
            setSelectedVacacionEmpleado(_vacaciones?.vacacion_empleado_periodo);
            setDisableFields(estado !== ESTATUS_ACTIVO);
            setVisible(true);
          });
        }
      });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // Edición directa desde la vista padre (hideTables): abre el registro indicado sin tabla.
  useEffect(() => {
    if (!autoEditRecordId) return;
    onClickEdit({ id: autoEditRecordId });
    // eslint-disable-next-line
  }, []);

  const onClickAdd = () => {
    form.resetFields();
    resetState();
    const vacacionEmpleadoPeriodo = vacacionesEmpleadosPeriodos.find((vep) => vep?.dias_habiles_pendientes > 0)
      ?? vacacionesEmpleadosPeriodos[0];
    setSelectedVacacionEmpleado(vacacionEmpleadoPeriodo?.id);
    form.setFieldsValue({
      fecha_captura: moment(),
      ano: vacacionEmpleadoPeriodo?.ano,
      dias_con_derecho: vacacionEmpleadoPeriodo?.dias_habiles_pendientes,
    });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const [key] = selectedRowKeys;
      const fechaInicialValue = form.getFieldValue('fecha_inicial');
      const fechaFinalValue = form.getFieldValue('fecha_final');
      const values = {
        ...form.getFieldsValue(),
        empleado: selectedEmpleado[0],
        vacacion_empleado_periodo: selectedVacacionEmpleado ?? vacacionesEmpleadosPeriodos[0]?.id,
        fecha_captura: moment(form.getFieldValue('fecha_captura')).format('YYYY-MM-DD'),
        fecha_inicial: fechaInicialValue ? moment(fechaInicialValue).format('YYYY-MM-DD') : null,
        fecha_final: fechaFinalValue ? moment(fechaFinalValue).format('YYYY-MM-DD') : null,
        // El campo "periodo" ya no se captura en el formulario. Se fija en 1 solo al
        // crear; al editar no se envía para no pisar el valor real de registros existentes.
        ...(key ? {} : { periodo: 1 }),
      };
      const saved = await (key
        ? patchVacacionesEmpleadoDiasDisfutados(key, values)
        : postVacacionesEmpleadoDiasDisfutados(values));
      if (saved) {
        await fetchData();
        // Al agregar un registro nuevo se mantiene el modal abierto en modo edición del
        // registro recién creado, para continuar con la selección de días (calendario).
        if (_continue || !key) {
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteVacacionesEmpleadoDiasDisfutados(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const cambiarEstado = async (estado) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key) {
        const saved = await patchVacacionesEmpleadoDiasDisfutados(key, { estados_globales: estado });
        if (saved) {
          await fetchData();
        }
      }
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Antigüedad',
      dataIndex: 'antiguedad_texto',
      key: 'antiguedad_texto',
      width: 200,
    },
    {
      titleText: 'Año de Vacaciones',
      dataIndex: 'configurar_guardias_vacacionales_general',
      key: 'configurar_guardias_vacacionales_general',
      width: 150,
      render: (_, record) => record?.configurar_guardias_vacacionales_general
        ?.periodo_fiscal?.periodo,
    },
    {
      titleText: 'Periodo Vacacional',
      dataIndex: 'configurar_guardias_vacacionales',
      key: 'configurar_guardias_vacacionales',
      width: 200,
      render: (_, record) => record?.configurar_guardias_vacacionales
        ?.periodos_vacacionales?.periodo,
    },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_captura',
      key: 'fecha_captura',
      width: 200,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : 'N/A'),
    },
    {
      titleText: 'Lugar de Trabajo y Actividad',
      dataIndex: 'lugar_trabajo_actividad',
      key: 'lugar_trabajo_actividad',
      width: 250,
    },
    {
      titleText: 'Días de Vacaciones',
      dataIndex: 'dias_seleccionados',
      key: 'dias_seleccionados',
      width: 250,
    },
    {
      titleText: 'No. de Días',
      dataIndex: 'dias_habiles_disfrutados',
      key: 'dias_habiles_disfrutados',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    validateDiasDisfrutados: [
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async () => {
          const { dias_habiles_disfrutados, dias_con_derecho } = form.getFieldsValue();
          if (dias_habiles_disfrutados > dias_con_derecho) {
            throw new Error('Este campo debe ser menor que Días con Derecho');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!hideTables && (
          <Col xs={24} sm={24} md={24}>
            <Text style={{ color: '#D88F4F' }}>Días de Vacaciones Disfrutadas</Text>
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit: () => onClickEdit(),
                onClickDelete,
              }}
              customActions={[
                {
                  onClick: () => setVisibleAutorizar(true),
                  permission: { model: 'vacacionempleadodiadisfrutado', action: 'autorizar' },
                  icon: <CheckCircleOutlined />,
                  text: 'Autorizar',
                  disabledBySelection: true,
                  disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
                  className: 'success',
                },
                {
                  onClick: () => setVisibleCancelar(true),
                  permission: { model: 'vacacionempleadodiadisfrutado', action: 'cancelar' },
                  icon: <CloseCircleOutlined />,
                  text: 'Cancelar',
                  disabledBySelection: true,
                  disabled: selectedData?.estados_globales !== ESTATUS_AUTORIZADO,
                  className: 'cancel',
                },
                {
                  onClick: () => setVisibleReactivar(true),
                  permission: { model: 'vacacionempleadodiadisfrutado', action: 'reactivar' },
                  icon: <RedoOutlined />,
                  text: 'Reactivar',
                  disabledBySelection: true,
                  disabled: ![ESTATUS_AUTORIZADO, ESTATUS_CANCELADO].includes(selectedData?.estados_globales),
                  className: 'success',
                },
              ]}
              disableEdit={selectedData?.estados_globales !== ESTATUS_ACTIVO}
              disableDelete={selectedData?.estados_globales !== ESTATUS_ACTIVO}
              permission={permission}
              allowExportParam={false}
              allowExportPDF={false}
            />
          </Col>
        )}
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={600}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Registro de Vacaciones`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="vacaciones_registros_empleados"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="fecha_captura"
                  label="Fecha de Captura"
                >
                  <DatePicker
                    disabled
                    placeholder=""
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="ano"
                  label="No. de Año de Vacaciones"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    dataSource={vacacionesEmpleadosPeriodos}
                    valueProp="ano"
                    render={(record) => record?.ano}
                    disabled={!!selectedRowKeys.length}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="configurar_guardias_vacacionales_general"
                  label="Año de Vacaciones"
                  hasFeedback
                >
                  <Select
                    dataSource={guardiasVacacionalGeneral}
                    render={(record) => record?.periodo_fiscal?.periodo}
                    disabled={!!selectedRowKeys.length}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="configurar_guardias_vacacionales"
                  label="Periodo Vacacional"
                  hasFeedback
                >
                  <Select
                    dataSource={periodosVacacionales}
                    render={(record) => record?.periodos_vacacionales?.periodo}
                    disabled={!!selectedRowKeys.length || !configurarGuardiasVacacionalesGeneralWatcher}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="dias_con_derecho"
                  label="Días Pendientes"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="lugar_trabajo_actividad"
                  label="Lugar de Trabajo y Actividad"
                  hasFeedback
                >
                  <Input
                    allowClear
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Form.Item name="fecha_inicial" hidden><input /></Form.Item>
              <Form.Item name="fecha_final" hidden><input /></Form.Item>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="fecha_inicial_vigencia_vacaciones"
                  label="Fecha Inicial de Vigencia de Vacaciones"
                  hasFeedback
                >
                  <DatePicker
                    placeholder=""
                    format="DD/MM/YYYY"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="fecha_final_vigencia_vacaciones"
                  label="Fecha Final de Vigencia de Vacaciones"
                  hasFeedback
                >
                  <DatePicker
                    placeholder=""
                    format="DD/MM/YYYY"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="dias_habiles_disfrutados"
                  label="Días Hábiles Disfrutados"
                  rules={rules.validateDiasDisfrutados}
                  disabled
                  hasFeedback
                />
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="observaciones"
                  label="Observaciones"
                  hasFeedback
                >
                  <TextArea
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    maxLength={250}
                    allowClear
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
          {open && !!selectedRowKeys.length && (
            <VacacionesRegistrosFechas
              formParent={form}
              parentId={selectedRowKeys[0]}
              permission={permission}
              dates={dates}
              disableFields={disableFields}
              empleado={selectedEmpleado[0]}
            />
          )}
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Eliminar Registro de Vacaciones"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => cambiarEstado(ESTATUS_AUTORIZADO)}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle="Autorizar el Registro de Vacaciones"
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => cambiarEstado(ESTATUS_CANCELADO)}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle="Cancelar el Registro de Vacaciones"
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => cambiarEstado(ESTATUS_ACTIVO)}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle="Reactivar el Registro de Vacaciones"
          open={visibleReactivar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default VacacionesRegistrosEmpleados;
